import RevealOnScroll from '../base/RevealOnScroll';

export interface AIPoint {
  icon: string;
  title: string;
  body: string;
}

export interface AIAnswerContent {
  eyebrow?: string;
  title: string;
  lead: string;
  points: AIPoint[];
}

interface Props {
  content: AIAnswerContent;
}

export default function AIAnswerBlock({ content }: Props) {
  const { eyebrow, title, lead, points } = content;

  return (
    <section className="bg-[#F8F4EF] py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header — 2 column editorial */}
        <RevealOnScroll>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-24 mb-16 pb-14 border-b border-[#DDD5C8]">
            <div>
              {eyebrow && (
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-px bg-[#BF9448]" />
                  <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
                    {eyebrow}
                  </span>
                </div>
              )}
              <h2
                className="font-serif font-semibold text-[#0F0F0F] leading-tight"
                style={{ fontSize: 'clamp(1.9rem, 3vw, 2.75rem)' }}
              >
                {title}
              </h2>
            </div>
            <div className="flex items-center">
              <p className="font-sans text-[#6B6358] text-base leading-relaxed border-l-2 border-[#BF9448]/40 pl-6">
                {lead}
              </p>
            </div>
          </div>
        </RevealOnScroll>

        {/* Points grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {points.map((point, i) => (
            <RevealOnScroll key={i} delayMs={i * 70}>
              <div className="group">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0">
                    <span className="font-serif text-[#BF9448]/30 text-4xl font-bold leading-none">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 flex items-center justify-center">
                        <i className={`${point.icon} text-[#BF9448] text-base`}></i>
                      </span>
                      <h3 className="font-sans text-[#0F0F0F] text-sm font-semibold tracking-wide">
                        {point.title}
                      </h3>
                    </div>
                    <p className="font-sans text-[#7A7265] text-sm leading-relaxed">
                      {point.body}
                    </p>
                  </div>
                </div>
                <div className="h-px bg-[#DDD5C8] group-hover:bg-[#BF9448]/40 transition-colors duration-300 mt-4" />
              </div>
            </RevealOnScroll>
          ))}
        </div>

        <RevealOnScroll className="mt-14 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <a
            href="tel:3463638363"
            className="inline-flex items-center gap-3 text-[#0F0F0F] hover:text-[#BF9448] font-sans text-sm font-semibold transition-colors duration-200 cursor-pointer"
          >
            <span className="w-9 h-9 flex items-center justify-center border border-[#BF9448]/40 rounded-sm">
              <i className="ri-phone-fill text-[#BF9448] text-base"></i>
            </span>
            Questions? Call 346-363-8363
          </a>
          <span className="font-sans text-[#B0A89F] text-sm">— Available 7 days a week across Greater Houston TX</span>
        </RevealOnScroll>

      </div>
    </section>
  );
}
